// React
import PropTypes from "prop-types";
import { useContext, useEffect, useState } from "react";

// @mui material components
import {
  createFilterOptions,
  Card,
  Checkbox,
  CircularProgress,
  Fade,
  FormControlLabel,
  Grid,
  Icon,
  InputLabel,
  Modal,
  Tab,
  Tabs,
  Tooltip,
} from "@mui/material";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";

import { gql, useLazyQuery, useMutation, useQuery } from "@apollo/client";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftInput from "components/SoftInput";
import SoftTypography from "components/SoftTypography";
import { MessageManager } from "context";
import { UserContext } from "context/user";
import Vehiculos from "../../../declaracion/Vehiculos";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  maxHeight: "90%",
  width: { xs: "90%", sm: "80%", xxl: "70%" },
  overflowY: "auto",
  p: 3,
};
const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;
const ACTIVIDADES = ["Generadora", "Generador Comercio/Servicio", "Transportista", "Operadora", "Operador in situ", "Recicladora/Operadora"];
const filtrarResiduos = createFilterOptions({ stringify: (residuo) => `${residuo.item} ${residuo.descripcion}` });
export default function ModalEmpresa({ open, handleClose, refetch, idEmpresa }) {
  const [tab, setTab] = useState(0);
  const [empresa, setEmpresa] = useState({});
  const [actividades, setActividades] = useState([]);
  const [vehiculos, setVehiculos] = useState([]);
  const [modalResiduos, setModalResiduos] = useState(false);
  const [seleccionResiduos, setSeleccionResiduos] = useState([]);
  const [busquedaResiduo, setBusquedaResiduo] = useState("");
  const [soloSeleccionados, setSoloSeleccionados] = useState(false);
  const { handleSnackbar } = useContext(MessageManager);
  const { user } = useContext(UserContext) || {};
  const esAdministrador = user?.tipoUser === "Administrador";
  const esTransportista = actividades.includes("Transportista");
  const esOperadora = actividades.some((actividad) => actividad.includes("Operador"));
  const selectorResiduosAbierto = open && modalResiduos && esAdministrador && esOperadora;
  const { data: dataResiduos, loading: loadingResiduos, error: errorResiduos } = useQuery(
    gql`
      query residuosHabilitacionEmpresa {
        residuos {
          id
          item
          descripcion
        }
      }
    `,
    { skip: !selectorResiduosAbierto, fetchPolicy: "no-cache" }
  );
  const idsHabilitados = empresa.idsResiduosHabilitados || [];
  const residuosVisibles = filtrarResiduos(dataResiduos?.residuos || [], { inputValue: busquedaResiduo.trim() })
    .filter((residuo) => !soloSeleccionados || seleccionResiduos.includes(residuo.id));

  useEffect(() => {
    if (open) {
      setTab(0);
      setModalResiduos(false);
    }
  }, [open]);

  const handleChanges = (e) => {
    setEmpresa({
      ...empresa,
      [e.target.name]: e.target.value,
    });
  };

  const [getEmpresa, { data: dataEmpresa }] = useLazyQuery(
    gql`
      query getEmpresa($id: ID!) {
        empresa(id: $id) {
          __typename @skip(if: true)
          id
          razonSocial
          cuit
          email
          actividad
          idsResiduosHabilitados
          gerente
          codigoAfip
          registroCatastral
          habilitacionMunicipalNumero
          habilitacionMunicipalLugar
          calleLegal
          localidadLegal
          cpLegal
          provinciaLegal
          departamentoLegal
          telefonoLegal
          faxLegal
          calleReal
          localidadReal
          cpReal
          provinciaReal
          departamentoReal
          telefonoReal
          faxReal
          vehiculos {
            id
            tipo
            marcaModelo
            dominio
            tallerPropio
          }
        }
      }
    `,
    {
      fetchPolicy: "no-cache",
    }
  );
  const [saveEmpresa, { loading: loadingSave }] = useMutation(
    gql`
      mutation saveEmpresa($input: EmpresaInput!) {
        saveEmpresa(input: $input) {
          id
        }
      }
    `
  );

  const handleSave = () => {
    const { idsResiduosHabilitados, ...datosEmpresa } = empresa;
    const input = {
      ...datosEmpresa,
      id: empresa?.id || null,
      actividad: actividades,
      ...(esAdministrador && esOperadora ? { idsResiduosHabilitados: idsResiduosHabilitados || [] } : {}),
    };

    if (esTransportista) {
      input.vehiculos = vehiculos
        .filter((vehiculo) => !vehiculo.aBorrar)
        .map(({ id, tipo, marcaModelo, dominio, tallerPropio }) => ({
          id,
          tipo,
          marcaModelo,
          dominio,
          tallerPropio,
        }));
    }

    saveEmpresa({
      variables: {
        input,
      },
    })
      .then(() => {
        refetch();
        handleSnackbar("Empresa guardada correctamente", "success");
        handleClose();
      })
      .catch((e) => {
        handleSnackbar(e.message || "Error al guardar la empresa", "error");
      });
  };
  useEffect(() => {
    if (idEmpresa && open) {
      getEmpresa({ variables: { id: idEmpresa } })
        .then((res) => {
          setActividades(res?.data?.empresa?.actividad || []);
        })
        .catch((e) => {
          handleSnackbar(e.message || "Error al obtener la empresa", "error");
        });
    } else if (open) {
      setActividades([]);
      setVehiculos([]);
    }
  }, [idEmpresa, open]);

  useEffect(() => {
    if (dataEmpresa && idEmpresa && open) {
      setEmpresa(dataEmpresa.empresa);
      setVehiculos(dataEmpresa.empresa.vehiculos || []);
    } else {
      setEmpresa({});
      setVehiculos([]);
    }
  }, [dataEmpresa, idEmpresa, open]);

  useEffect(() => {
    if (!esTransportista && tab === 3) setTab(0);
  }, [esTransportista, tab]);

  return (
    <>
    <Modal
      aria-labelledby="transition-modal-title"
      aria-describedby="transition-modal-description"
      open={open}
      onClose={handleClose}
      closeAfterTransition
    >
      <Fade in={open}>
        <Card sx={style}>
          <SoftBox>
            <SoftBox display="flex" justifyContent="space-between" alignItems="center">
              <SoftTypography variant="h6">
                {empresa?.id ? "Editar Empresa" : "Agregar Empresa"}
              </SoftTypography>
              <Tooltip title="Cerrar" placement="top">
                <Icon fontSize="medium" onClick={handleClose} sx={{ cursor: "pointer" }}>
                  close
                </Icon>
              </Tooltip>
            </SoftBox>
            <Tabs
              value={tab}
              onChange={(_, value) => setTab(value)}
              variant="fullWidth"
              aria-label="Datos de la empresa"
              sx={{ mt: 2 }}
            >
              {[
                "Datos generales",
                "Domicilio legal",
                "Domicilio real",
                ...(esTransportista ? ["Vehículos"] : []),
              ].map((label, index) => (
                <Tab
                  key={label}
                  label={label}
                  id={`empresa-tab-${index}`}
                  aria-controls={`empresa-panel-${index}`}
                />
              ))}
            </Tabs>
            <SoftBox py={3}>
              <div
                role="tabpanel"
                id="empresa-panel-0"
                aria-labelledby="empresa-tab-0"
                hidden={tab !== 0}
              >
                <Grid container spacing={2}>
                  {/* Existing fields */}
                  <Grid item xs={12} sm={4}>
                    <InputLabel htmlFor="razonSocial">
                      <SoftTypography component="label" variant="caption" fontWeight="bold">
                        Razón Social
                        <SoftTypography variant="caption" fontWeight="bold" color="primary">
                          &nbsp;*
                        </SoftTypography>
                      </SoftTypography>
                    </InputLabel>
                    <SoftInput
                      label="razonSocial"
                      placeholder="Ingrese la razón social"
                      type="text"
                      name="razonSocial"
                      value={empresa?.razonSocial || ""}
                      onChange={handleChanges}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <InputLabel htmlFor="cuit">
                      <SoftTypography component="label" variant="caption" fontWeight="bold">
                        CUIT
                        <SoftTypography variant="caption" fontWeight="bold" color="primary">
                          &nbsp;*
                        </SoftTypography>
                      </SoftTypography>
                    </InputLabel>
                    <SoftInput
                      label="cuit"
                      placeholder="Ingrese el CUIT"
                      type="number"
                      name="cuit"
                      value={empresa?.cuit || ""}
                      onChange={handleChanges}
                      inputProps={{ min: 0, step: 1 }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <InputLabel htmlFor="email">
                      <SoftTypography component="label" variant="caption" fontWeight="bold">
                        Email
                        <SoftTypography variant="caption" fontWeight="bold" color="primary">
                          &nbsp;*
                        </SoftTypography>
                      </SoftTypography>
                    </InputLabel>
                    <SoftInput
                      label="email"
                      placeholder="Ingrese el email"
                      type="email"
                      name="email"
                      value={empresa?.email || ""}
                      onChange={handleChanges}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <SoftTypography id="actividades-label" component="div" variant="caption" fontWeight="bold" mb={1}>
                      Actividades
                    </SoftTypography>
                    <SoftBox role="group" aria-labelledby="actividades-label" borderRadius="md" sx={{
                      display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))", lg: "repeat(3, minmax(0, 1fr))" },
                      border: 1, borderColor: "grey.300", p: 1,
                    }}>
                      {[...new Set([...ACTIVIDADES, ...actividades])].map((actividad) => (
                        <FormControlLabel key={actividad} sx={{ m: 0, minHeight: 44 }}
                          control={<Checkbox icon={icon} checkedIcon={checkedIcon} checked={actividades.includes(actividad)}
                            onChange={(_, checked) => setActividades((prev) => checked ? [...prev, actividad] : prev.filter((item) => item !== actividad))} />}
                          label={<SoftTypography variant="body2" color="dark">{actividad}</SoftTypography>}
                        />
                      ))}
                    </SoftBox>
                  </Grid>
                  {esAdministrador && esOperadora && (
                    <Grid item xs={12}>
                      <SoftTypography id="residuos-habilitados-label" component="div" variant="caption" fontWeight="bold" mb={1}>
                        Residuos habilitados para disponer/operar
                      </SoftTypography>
                      <SoftBox display="flex" flexDirection={{ xs: "column", sm: "row" }} gap={1}
                        justifyContent="space-between" alignItems={{ xs: "stretch", sm: "center" }}
                        borderRadius="md" sx={{ border: 1, borderColor: "grey.300", p: 1.5 }}>
                        <SoftTypography component="div" variant="body2" color="dark" aria-live="polite">
                          {idsHabilitados.length} {idsHabilitados.length === 1 ? "residuo seleccionado" : "residuos seleccionados"}
                        </SoftTypography>
                        <SoftButton size="small" variant="outlined" color="primary" onClick={() => {
                          setSeleccionResiduos([...idsHabilitados]);
                          setBusquedaResiduo("");
                          setSoloSeleccionados(false);
                          setModalResiduos(true);
                        }}>
                          {idsHabilitados.length ? "Modificar selección" : "Seleccionar residuos"}
                        </SoftButton>
                      </SoftBox>
                    </Grid>
                  )}
                  <Grid item xs={12} sm={4}>
                    <InputLabel htmlFor="gerente">
                      <SoftTypography component="label" variant="caption" fontWeight="bold">
                        Gerente
                      </SoftTypography>
                    </InputLabel>
                    <SoftInput
                      label="gerente"
                      placeholder="Ingrese el nombre del gerente"
                      type="text"
                      name="gerente"
                      value={empresa?.gerente || ""}
                      onChange={handleChanges}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <InputLabel htmlFor="codigoAfip">
                      <SoftTypography component="label" variant="caption" fontWeight="bold">
                        Código AFIP
                      </SoftTypography>
                    </InputLabel>
                    <SoftInput
                      label="codigoAfip"
                      placeholder="Ingrese el código AFIP"
                      type="number"
                      name="codigoAfip"
                      value={empresa?.codigoAfip || ""}
                      onChange={handleChanges}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <InputLabel htmlFor="registroCatastral">
                      <SoftTypography component="label" variant="caption" fontWeight="bold">
                        Registro Catastral
                      </SoftTypography>
                    </InputLabel>
                    <SoftInput
                      label="registroCatastral"
                      placeholder="Ingrese el registro catastral"
                      type="number"
                      name="registroCatastral"
                      value={empresa?.registroCatastral || ""}
                      onChange={handleChanges}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <InputLabel htmlFor="habilitacionMunicipalNumero">
                      <SoftTypography component="label" variant="caption" fontWeight="bold">
                        Habilitación Municipal Número
                      </SoftTypography>
                    </InputLabel>
                    <SoftInput
                      label="habilitacionMunicipalNumero"
                      placeholder="Ingrese el número de habilitación municipal"
                      type="number"
                      name="habilitacionMunicipalNumero"
                      value={empresa?.habilitacionMunicipalNumero || ""}
                      onChange={handleChanges}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <InputLabel htmlFor="habilitacionMunicipalLugar">
                      <SoftTypography component="label" variant="caption" fontWeight="bold">
                        Habilitación Municipal Lugar
                      </SoftTypography>
                    </InputLabel>
                    <SoftInput
                      label="habilitacionMunicipalLugar"
                      placeholder="Ingrese el lugar de habilitación municipal"
                      type="text"
                      name="habilitacionMunicipalLugar"
                      value={empresa?.habilitacionMunicipalLugar || ""}
                      onChange={handleChanges}
                    />
                  </Grid>
                </Grid>
              </div>
              {["Legal", "Real"].map((domicilio, index) => (
                <div
                  key={domicilio}
                  role="tabpanel"
                  id={`empresa-panel-${index + 1}`}
                  aria-labelledby={`empresa-tab-${index + 1}`}
                  hidden={tab !== index + 1}
                >
                  <Grid container spacing={2}>
                    {[
                      ["calle", "Calle"],
                      ["localidad", "Localidad"],
                      ["cp", "Código postal"],
                      ["provincia", "Provincia"],
                      ["departamento", "Departamento"],
                      ["telefono", "Teléfono"],
                      ["fax", "Fax"],
                    ].map(([campo, etiqueta]) => {
                      const name = `${campo}${domicilio}`;
                      return (
                        <Grid item xs={12} sm={4} key={name}>
                          <InputLabel htmlFor={name}>
                            <SoftTypography component="span" variant="caption" fontWeight="bold">
                              {etiqueta}
                            </SoftTypography>
                          </InputLabel>
                          <SoftInput
                            id={name}
                            name={name}
                            type={campo === "telefono" || campo === "fax" ? "tel" : "text"}
                            value={empresa?.[name] || ""}
                            onChange={handleChanges}
                            fullWidth
                          />
                        </Grid>
                      );
                    })}
                  </Grid>
                </div>
              ))}
              {esTransportista && (
                <div
                  role="tabpanel"
                  id="empresa-panel-3"
                  aria-labelledby="empresa-tab-3"
                  hidden={tab !== 3}
                >
                  <Vehiculos
                    listadoVehiculos={vehiculos}
                    setListadoVehiculos={setVehiculos}
                    finalizada={false}
                    idEmpresa={empresa?.id}
                    mostrarAnteriores={false}
                  />
                </div>
              )}
            </SoftBox>
            <SoftBox display="flex" justifyContent="flex-end" alignItems="center">
              <SoftButton color="primary" onClick={handleClose} sx={{ marginRight: 1 }}>
                <Icon sx={{ fontWeight: "light" }}>cancel</Icon>
                &nbsp;Cancelar
              </SoftButton>
              <SoftButton
                color="gobSecondary"
                disabled={loadingSave || !empresa?.razonSocial || !empresa?.cuit || !empresa?.email}
                onClick={handleSave}
              >
                {loadingSave ? (
                  <CircularProgress size={15} sx={{ marginRight: 0.5 }} color="inherit" />
                ) : (
                  <Icon sx={{ fontWeight: "light" }}>save</Icon>
                )}
                &nbsp;Guardar
              </SoftButton>
            </SoftBox>
          </SoftBox>
        </Card>
      </Fade>
    </Modal>
    <Modal open={selectorResiduosAbierto}
      onClose={() => setModalResiduos(false)} aria-labelledby="seleccionar-residuos-title" closeAfterTransition>
      <Fade in={selectorResiduosAbierto}>
        <Card role="dialog" aria-modal="true" aria-labelledby="seleccionar-residuos-title"
          sx={{ ...style, display: "flex", flexDirection: "column" }}>
          <SoftBox display="flex" justifyContent="space-between" alignItems="center" gap={2} mb={2}>
            <SoftBox>
              <SoftTypography id="seleccionar-residuos-title" variant="h6">Seleccionar residuos habilitados</SoftTypography>
              <SoftTypography variant="caption" color="text">{seleccionResiduos.length} seleccionados</SoftTypography>
            </SoftBox>
            <SoftButton variant="text" color="dark" aria-label="Cerrar selección de residuos"
              onClick={() => setModalResiduos(false)} sx={{ minWidth: 44 }}>
              <Icon fontSize="small">close</Icon>
            </SoftButton>
          </SoftBox>
          <SoftBox borderRadius="md" sx={{ border: 1, borderColor: "grey.300", p: 1.5 }}>
            <Grid container spacing={1} alignItems="center" mb={1}>
              <Grid item xs={12} sm>
                <SoftInput placeholder="Buscar por código o descripción"
                  inputProps={{ "aria-label": "Buscar residuos habilitados" }}
                  icon={{ component: "search", direction: "left" }} value={busquedaResiduo}
                  disabled={loadingResiduos || Boolean(errorResiduos)}
                  onChange={(event) => setBusquedaResiduo(event.target.value)} />
              </Grid>
              <Grid item xs={12} sm="auto">
                <SoftButton size="small" variant="outlined" color="primary"
                  disabled={loadingResiduos || Boolean(errorResiduos) || (!soloSeleccionados && seleccionResiduos.length === 0)}
                  aria-pressed={soloSeleccionados} onClick={() => setSoloSeleccionados((prev) => !prev)}>
                  {soloSeleccionados ? "Ver todos" : `Ver seleccionados (${seleccionResiduos.length})`}
                </SoftButton>
              </Grid>
            </Grid>
            <SoftBox id="residuos-habilitados" role="group" aria-label="Residuos disponibles"
              sx={{ height: { xs: "40vh", sm: "50vh" }, overflowY: "auto", "& > label + label": { borderTop: 1, borderColor: "grey.200" } }}>
              {loadingResiduos ? (
                <SoftBox display="flex" alignItems="center" gap={1} p={1}>
                  <CircularProgress size={16} />
                  <SoftTypography variant="caption">Cargando residuos...</SoftTypography>
                </SoftBox>
              ) : errorResiduos ? (
                <SoftTypography role="alert" variant="caption" color="error">
                  No se pudo cargar el catálogo de residuos. Vuelva a abrir el formulario.
                </SoftTypography>
              ) : residuosVisibles.length === 0 ? (
                <SoftTypography component="div" variant="caption" p={1}>
                  {soloSeleccionados && seleccionResiduos.length === 0 ? "No hay residuos seleccionados" : "No hay residuos coincidentes"}
                </SoftTypography>
              ) : residuosVisibles.map((residuo) => (
                <FormControlLabel key={residuo.id} sx={{
                  display: "flex", alignItems: "flex-start", m: 0, py: 0.5, minHeight: 44,
                  bgcolor: seleccionResiduos.includes(residuo.id) ? "grey.100" : "transparent",
                  "& .MuiFormControlLabel-label": { minWidth: 0, pt: 0.75, pr: 1 },
                }} control={<Checkbox icon={icon} checkedIcon={checkedIcon} checked={seleccionResiduos.includes(residuo.id)}
                  onChange={(_, checked) => setSeleccionResiduos((prev) => checked
                    ? [...prev, residuo.id]
                    : prev.filter((id) => id !== residuo.id))} />}
                  label={<SoftTypography component="span" variant="body2" color="dark" sx={{ overflowWrap: "anywhere" }}>
                    <SoftTypography component="span" variant="body2" fontWeight="bold" color="dark" mr={1}>{residuo.item}</SoftTypography>{" "}
                    {residuo.descripcion}
                  </SoftTypography>} />
              ))}
            </SoftBox>
          </SoftBox>
          <SoftBox display="flex" justifyContent="flex-end" gap={1} mt={2}>
            <SoftButton color="primary" onClick={() => setModalResiduos(false)}>Cancelar</SoftButton>
            <SoftButton color="gobSecondary" disabled={loadingResiduos || Boolean(errorResiduos)} onClick={() => {
              setEmpresa((prev) => ({ ...prev, idsResiduosHabilitados: [...seleccionResiduos] }));
              setModalResiduos(false);
            }}>Aceptar selección</SoftButton>
          </SoftBox>
        </Card>
      </Fade>
    </Modal>
    </>
  );
}

ModalEmpresa.propTypes = {
  open: PropTypes.bool,
  handleClose: PropTypes.func,
  refetch: PropTypes.func,
  idEmpresa: PropTypes.string,
};
